/**
 * Единый источник правды для форматирования в веб-букинге.
 * Используется в BookingPage, ManagePage, SuccessPage.
 */
import {
  formatDurationLocalized,
  formatPrice as formatPriceShared,
  type FormatDurationLocale,
} from '@minibook/shared/utils/formatters';

/** Локаль для цен и чисел (ru-RU / en-US) */
export type PriceLocale = FormatDurationLocale;

/** Язык i18n (например из useTranslation) → локаль для цен. Один источник правды для всех страниц. */
export function getPriceLocale(language: string): PriceLocale {
  switch (language) {
    case 'en':
    case 'uz':
    case 'tg':
    case 'ky':
    case 'az':
    case 'hy':
    case 'kk':
    case 'hi':
    case 'it':
    case 'ro':
    case 'ru':
      return language;
    default:
      return 'ru';
  }
}

export function getIntlLocale(language: string): string {
  const localeTagByLanguage: Record<PriceLocale, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    uz: 'uz-UZ',
    tg: 'tg-TJ',
    ky: 'ky-KG',
    az: 'az-AZ',
    hy: 'hy-AM',
    kk: 'kk-KZ',
    hi: 'hi-IN',
    it: 'it-IT',
    ro: 'ro-RO',
  };
  return localeTagByLanguage[getPriceLocale(language)];
}

/**
 * Продолжительность в минутах → "X ч Y мин" / "X ч" / "Y мин".
 */
export function formatDuration(minutes?: number | null, locale: PriceLocale = 'ru'): string {
  return formatDurationLocalized(minutes, locale);
}

/**
 * Число (цена) → отформатированная строка с локалью.
 * Не включает символ валюты — его добавляет вызывающий код через i18n (t('booking.currency')).
 */
export function formatPrice(value: number, locale: PriceLocale): string {
  return formatPriceShared(value, { locale: getIntlLocale(locale), suffix: false });
}
